/**
 * Data table wrapper around @tanstack/react-table.
 *
 * Caller-facing API is intentionally narrow:
 *   - columns: array of `DataTableColumn<T>` (id, header, accessor, cell,
 *     sortable, defaultHidden, width, align)
 *   - searchText(row): string fed into the global filter input
 *   - filters / actions: arbitrary nodes slotted into the toolbar
 *
 * Visual reuses bm-table / bm-toolbar tokens.
 */

import {
  type ColumnDef,
  type RowSelectionState,
  type SortingState,
  type VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from './brand';

export interface DataTableColumn<T> {
  id: string;
  header: string;
  accessor?: (row: T) => unknown;
  cell?: (row: T) => ReactNode;
  sortable?: boolean;
  defaultHidden?: boolean;
  width?: number | string;
  align?: 'left' | 'right';
}

interface Props<T> {
  data: T[];
  columns: DataTableColumn<T>[];
  rowKey: (row: T) => string;
  searchText?: (row: T) => string;
  searchPlaceholder?: string;
  filters?: ReactNode;
  emptyLabel?: ReactNode;
  loading?: boolean;
  actions?: ReactNode;
  /** Enable a checkbox column. Caller observes selection via onSelectionChange. */
  enableSelection?: boolean;
  /** Callback fired when the row-selection set changes. */
  onSelectionChange?: (selectedIds: string[]) => void;
  /** When true, the row checkbox renders disabled. */
  isRowSelectable?: (row: T) => boolean;
}

export function DataTable<T>({
  data,
  columns,
  rowKey,
  searchText,
  searchPlaceholder = 'Search…',
  filters,
  emptyLabel = 'No rows.',
  loading = false,
  actions,
  enableSelection = false,
  onSelectionChange,
  isRowSelectable,
}: Props<T>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(() =>
    Object.fromEntries(columns.filter((c) => c.defaultHidden).map((c) => [c.id, false])),
  );
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const tsColumns = useMemo<ColumnDef<T>[]>(() => {
    const base: ColumnDef<T>[] = columns.map<ColumnDef<T>>((c) => ({
      id: c.id,
      header: c.header,
      ...(c.accessor ? { accessorFn: (row) => c.accessor?.(row) } : {}),
      enableSorting: !!c.sortable,
      enableHiding: c.id !== 'rowActions',
      cell: c.cell ? (ctx) => c.cell?.(ctx.row.original) : (ctx) => String(ctx.getValue() ?? ''),
      meta: { width: c.width, align: c.align },
    }));
    if (!enableSelection) return base;
    const selectionCol: ColumnDef<T> = {
      id: '__select',
      enableSorting: false,
      enableHiding: false,
      header: ({ table }) => (
        <SelectAllCheckbox
          checked={table.getIsAllRowsSelected()}
          indeterminate={table.getIsSomeRowsSelected()}
          onChange={table.getToggleAllRowsSelectedHandler()}
        />
      ),
      cell: ({ row }) => (
        <SelectRowCheckbox
          checked={row.getIsSelected()}
          disabled={!row.getCanSelect()}
          onChange={row.getToggleSelectedHandler()}
        />
      ),
      meta: { width: 32 },
    };
    return [selectionCol, ...base];
  }, [columns, enableSelection]);

  const table = useReactTable<T>({
    data,
    columns: tsColumns,
    state: { sorting, globalFilter, columnVisibility, rowSelection },
    enableRowSelection: enableSelection
      ? (row) => (isRowSelectable ? isRowSelectable(row.original) : true)
      : false,
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getRowId: (row) => rowKey(row),
    globalFilterFn: searchText
      ? (row, _columnId, value) => {
          const v = String(value ?? '')
            .toLowerCase()
            .trim();
          if (!v) return true;
          return searchText(row.original).toLowerCase().includes(v);
        }
      : 'auto',
  });

  const visibleColCount = table.getVisibleLeafColumns().length;

  // biome-ignore lint/correctness/useExhaustiveDependencies: onSelectionChange is the consumer hook
  useEffect(() => {
    if (!onSelectionChange) return;
    onSelectionChange(Object.keys(rowSelection).filter((k) => rowSelection[k]));
  }, [rowSelection]);

  return (
    <div>
      <div className="bm-toolbar">
        <div className="bm-toolbar-search">
          <Icon.search size={12} />
          <input
            type="text"
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            placeholder={searchPlaceholder}
          />
        </div>

        {filters}

        <span className="bm-spacer" />

        <ColumnsToggle table={table} columnMeta={columns} />

        {actions}
      </div>

      <div className="bm-card-flush">
        <table className="bm-table">
          <thead>
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {hg.headers.map((header) => {
                  const meta = header.column.columnDef.meta as
                    | { width?: number | string; align?: 'left' | 'right' }
                    | undefined;
                  const canSort = header.column.getCanSort();
                  const sortDir = header.column.getIsSorted();
                  const arrow = sortDir === 'asc' ? '▲' : sortDir === 'desc' ? '▼' : '';
                  return (
                    <th
                      key={header.id}
                      style={{
                        width: meta?.width,
                        textAlign: meta?.align ?? 'left',
                        cursor: canSort ? 'pointer' : 'default',
                        userSelect: 'none',
                      }}
                      onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {canSort && (
                          <span
                            style={{
                              color: sortDir ? 'var(--bm-text)' : 'var(--bm-text-faint)',
                              fontSize: 9,
                            }}
                          >
                            {arrow || '↕'}
                          </span>
                        )}
                      </span>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td
                  colSpan={visibleColCount}
                  style={{ padding: 24, textAlign: 'center', color: 'var(--bm-text-muted)' }}
                >
                  Loading…
                </td>
              </tr>
            )}
            {!loading && table.getRowModel().rows.length === 0 && (
              <tr>
                <td
                  colSpan={visibleColCount}
                  style={{ padding: 24, textAlign: 'center', color: 'var(--bm-text-muted)' }}
                >
                  {globalFilter ? `No matches for "${globalFilter}".` : emptyLabel}
                </td>
              </tr>
            )}
            {!loading &&
              table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => {
                    const meta = cell.column.columnDef.meta as
                      | { align?: 'left' | 'right' }
                      | undefined;
                    return (
                      <td key={cell.id} style={{ textAlign: meta?.align ?? 'left' }}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    );
                  })}
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ─── Column visibility popover ─────────────────────────────────── */

function ColumnsToggle<T>({
  table,
  columnMeta,
}: {
  table: ReturnType<typeof useReactTable<T>>;
  columnMeta: DataTableColumn<T>[];
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const labelFor = (id: string) => columnMeta.find((c) => c.id === id)?.header || id;

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button
        type="button"
        className="bm-btn bm-btn-ghost bm-btn-sm"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <Icon.filter size={12} /> Columns
      </button>
      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            right: 0,
            zIndex: 30,
            minWidth: 200,
            background: 'var(--bm-surface-1)',
            border: '1px solid var(--bm-border)',
            borderRadius: 'var(--bm-r-md)',
            boxShadow: 'var(--bm-elev-pop)',
            padding: 6,
          }}
        >
          {table
            .getAllLeafColumns()
            .filter((col) => col.id !== '__select' && col.getCanHide())
            .map((col) => {
              const visible = col.getIsVisible();
              return (
                <button
                  key={col.id}
                  type="button"
                  className="bm-combobox-item"
                  data-active={false}
                  role="menuitemcheckbox"
                  aria-checked={visible}
                  onClick={() => col.toggleVisibility()}
                >
                  <span className="bm-toggle" data-on={visible} style={{ pointerEvents: 'none' }} />
                  <span style={{ flex: 1, textAlign: 'left' }}>{labelFor(col.id)}</span>
                </button>
              );
            })}
        </div>
      )}
    </div>
  );
}

/* ─── selection checkboxes ─────────────────────────────────────── */

function SelectAllCheckbox({
  checked,
  indeterminate,
  onChange,
}: {
  checked: boolean;
  indeterminate: boolean;
  onChange: (e: unknown) => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate && !checked;
  }, [indeterminate, checked]);
  return (
    <span className="bm-checkbox" data-checked={checked}>
      <input
        ref={ref}
        type="checkbox"
        aria-label="Select all"
        checked={checked}
        onChange={(e) => onChange(e)}
      />
      {indeterminate && !checked ? (
        <span style={{ fontSize: 9, pointerEvents: 'none' }}>—</span>
      ) : checked ? (
        <Icon.check size={10} />
      ) : null}
    </span>
  );
}

function SelectRowCheckbox({
  checked,
  disabled,
  onChange,
}: {
  checked: boolean;
  disabled: boolean;
  onChange: (e: unknown) => void;
}) {
  return (
    <span className="bm-checkbox" data-checked={checked} data-disabled={disabled}>
      <input
        type="checkbox"
        aria-label="Select row"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e)}
      />
      {checked && <Icon.check size={10} />}
    </span>
  );
}
